import { Fragment, useState, useEffect } from "react";
import { Dialog, Popover, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = "https://app.salesboxx.store/api/simfinance";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showPartnersDropdown, setShowPartnersDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navigation = {
    pages: [{ name: "About Us", href: "/about" }],
  };

  useEffect(() => {
    const fetchCategoriesAndPartners = async () => {
      try {
        const categoriesResponse = await axios.get(
          `${API_BASE_URL}/allCategories`
        );
        const categoriesData = categoriesResponse.data;

        const partnersData = await Promise.all(
          categoriesData.map(async (category) => {
            const partnersResponse = await axios.post(
              `${API_BASE_URL}/getCategoryPartners`,
              { category_id: category.id }
            );
            return { ...category, partners: partnersResponse.data };
          })
        );

        setCategories(partnersData);
      } catch (error) {
        console.error("Error fetching categories and partners:", error);
      }
    };
    fetchCategoriesAndPartners();
  }, []);

  const fetchPartnerDetails = async (partnerId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/getPartner`, {
        id: partnerId,
      });
      const partnerDetails = response.data;

      navigate(`/partner/${partnerDetails.data.partner_info.partner_name}`, {
        state: { partnerDetails },
      });
    } catch (error) {
      console.error("Error fetching partner details:", error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <div
      className={classNames(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled ? "bg-white shadow-md" : "bg-transparent"
      )}
    >
      {/* Mobile menu */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setMobileMenuOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex flex-col w-full max-w-xs pb-12 overflow-y-auto bg-white shadow-xl">
                <div className="flex px-4 pt-5 pb-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center p-2 -m-2 text-gray-400 rounded-md"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Logo in Mobile Menu */}
                <div className="flex items-center justify-center mb-4">
                  <NavLink to="/">
                    <h1 className="text-2xl font-bold">
                      <span className="text-purple-600">Sim</span>
                      <span className="text-orange-500">Finance</span>
                    </h1>
                  </NavLink>
                </div>

                {/* Mobile navigation */}
                <div className="px-4 py-6 space-y-6 border-t border-gray-200">
                  {navigation.pages.map((page) => (
                    <NavLink
                      key={page.name}
                      to={page.href}
                      className={classNames(
                        "text-sm font-medium transition-colors duration-200 ease-out",
                        isScrolled ? "text-black hover:text-gray-800" : ""
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {page.name}
                    </NavLink>
                  ))}

                  {/* Partner Dropdown */}
                  <div className="flow-root">
                    <button
                      onClick={() =>
                        setShowPartnersDropdown(!showPartnersDropdown)
                      }
                      className="p-2 -m-2 font-medium text-gray-900 flex justify-between items-center w-full"
                    >
                      Partner
                      <ChevronDownIcon
                        className={`ml-2 h-4 w-4 transform transition-transform ${
                          showPartnersDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {showPartnersDropdown && (
                      <div className="mt-2">
                        {categories.map((category) => (
                          <div key={category.id} className="flow-root">
                            <button className="block p-2 -m-2 font-medium text-gray-900">
                              {category.name}
                            </button>
                            {/* Automatically display partners for each category */}
                            <ul className="mt-2 space-y-2">
                              {category.partners.map((partner) => (
                                <li key={partner.id}>
                                  <button
                                    onClick={() => {
                                      fetchPartnerDetails(partner.id);
                                      setMobileMenuOpen(false);
                                    }}
                                    className="text-left transition-colors hover:text-purple-600"
                                  >
                                    {partner.partner_name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop menu */}
      <header className="relative">
        <nav aria-label="Top">
          <div
            className={classNames(
              "transition-colors duration-300",
              isScrolled ? "bg-white" : "bg-transparent"
            )}
          >
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-20">
                {/* Logo for Desktop */}
                <div className="flex items-center flex-1">
                  <NavLink to="/">
                    <h1 className="text-2xl font-bold">
                      <span
                        className={
                          isScrolled ? "text-purple-600" : "text-white"
                        }
                      >
                        Sim
                      </span>
                      <span
                        className={
                          isScrolled ? "text-orange-500" : "text-orange-400"
                        }
                      >
                        Finance
                      </span>
                    </h1>
                  </NavLink>
                </div>

                {/* Mega menus */}
                <Popover.Group className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-center lg:space-x-12">
                  <Popover className="flex">
                    {({ open }) => (
                      <>
                        <div className="relative flex">
                          <Popover.Button
                            className={classNames(
                              "relative z-10 flex items-center justify-center text-sm font-medium transition-colors duration-200 ease-out",
                              open
                                ? "text-purple-600"
                                : isScrolled
                                ? "text-gray-700 hover:text-gray-800"
                                : "text-white hover:text-gray-200"
                            )}
                          >
                            Partner
                            <ChevronDownIcon
                              className={classNames(
                                "ml-2 h-4 w-4",
                                open
                                  ? "text-purple-600"
                                  : isScrolled
                                  ? "text-gray-400"
                                  : "text-white",
                                "group-hover:text-gray-500"
                              )}
                              aria-hidden="true"
                            />
                          </Popover.Button>
                        </div>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="opacity-0 translate-y-1"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-1"
                        >
                          <Popover.Panel className="absolute left-auto right-auto z-10 text-sm text-gray-500 transform mt-14">
                            <div
                              className="absolute inset-0 bg-white shadow-lg rounded-b-xl"
                              aria-hidden="true"
                            />
                            <div className="relative bg-white rounded-b-xl">
                              <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                                <div className="grid grid-cols-1 py-16 gap-x-8 gap-y-10 sm:grid-cols-2 md:grid-cols-2">
                                  <div className="col-span-2 col-start-1 sm:col-start-1">
                                    <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2 md:grid-cols-3">
                                      {categories.map((category) => (
                                        <div key={category.id}>
                                          <p className="font-medium text-gray-900">
                                            {category.name}
                                          </p>
                                          <ul className="mt-6 space-y-6 sm:mt-4 sm:space-y-4">
                                            {category.partners.map(
                                              (partner) => (
                                                <li
                                                  key={partner.id}
                                                  className="flex"
                                                >
                                                  <button
                                                    onClick={() =>
                                                      fetchPartnerDetails(
                                                        partner.id
                                                      )
                                                    }
                                                    className="text-left transition-colors hover:text-purple-600"
                                                  >
                                                    {partner.partner_name}
                                                  </button>
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Popover.Panel>
                        </Transition>
                      </>
                    )}
                  </Popover>

                  {navigation.pages.map((page) => (
                    <NavLink
                      key={page.name}
                      to={page.href}
                      className={classNames(
                        "text-sm font-medium transition-colors duration-200 ease-out",
                        isScrolled
                          ? "text-gray-700 hover:text-gray-800"
                          : "text-white hover:text-gray-200"
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {page.name}
                    </NavLink>
                  ))}
                </Popover.Group>

                <div className="flex items-center justify-end flex-1">
                  {/* Mobile menu button */}
                  <button
                    type="button"
                    className={classNames(
                      "relative inline-flex items-center justify-center p-2 -m-2 rounded-md lg:hidden",
                      isScrolled ? "text-gray-400" : "text-white"
                    )}
                    onClick={() => setMobileMenuOpen(true)}
                  >
                    <span className="sr-only">Open menu</span>
                    <Bars3Icon className="w-6 h-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </div>
  );
};

export default Navbar;
